/**
 * nodeTypeIcons.tsx — Shared SVG icon constants per node type.
 *
 * Exports:
 * - NODE_TYPE_ICON: Record<string, ReactNode>  — React elements for JSX contexts
 * - NODE_TYPE_ICON_SVG: Record<string, string>  — Raw SVG strings for DOM innerHTML
 *
 * Icons are clean gray strokes on a transparent background, designed for
 * reference chips (12px), floating labels (14px), and dropdown items (14px).
 */

import React from 'react'

// ─── SVG markup strings (for raw DOM contexts like createMentionChipElement) ──

const CHARACTER_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="10" r="6"/><path d="M6 8.5a1.5 1.5 0 000 3"/><path d="M18 8.5a1.5 1.5 0 010 3"/><path stroke-linecap="round" d="M8 20c0-2.5 1.8-4 4-4s4 1.5 4 4"/><path stroke-linecap="round" d="M9.5 9.5c.3.6.8.6 1.2 0"/><path stroke-linecap="round" d="M13.3 9.5c.3.6.8.6 1.2 0"/></svg>`

const LOCATION_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" stroke-width="1.5"/></svg>`

const OBJECT_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linejoin="round" stroke-width="1.5" d="M12 2L3 7v10l9 5 9-5V7l-9-5z"/><path stroke-width="1.5" d="M12 12L3 7m9 5l9-5m-9 5v10"/></svg>`

const IMAGE_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="3" width="18" height="18" rx="3" stroke-width="1.5"/><path d="M3 17l6-7 12 8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><circle cx="16" cy="8.5" r="2" stroke-width="1.5"/></svg>`

const VIDEO_SVG = `<svg viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" stroke-width="1.5"/><path d="M10 8.5v7l6-3.5-6-3.5z" fill="currentColor"/></svg>`

const AUDIO_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-width="2" d="M7 12c0 0 .5-4 1.5-4S10 16 11 16s1-8 2-8 1 8 2 8 1-4 1.5-4 .5 0 .5 0"/></svg>`

const SCRIPT_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>`

const GROUP_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="4" y="4" width="7" height="7" rx="1.5" stroke-width="1.5"/><rect x="13" y="4" width="7" height="7" rx="1.5" stroke-width="1.5"/><rect x="4" y="13" width="7" height="7" rx="1.5" stroke-width="1.5"/><rect x="13" y="13" width="7" height="7" rx="1.5" stroke-width="1.5"/></svg>`

// ─── React icon elements ────────────────────────────────────────────────────

const CharacterIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <circle cx={12} cy={10} r={6} />
    <path d="M6 8.5a1.5 1.5 0 000 3" />
    <path d="M18 8.5a1.5 1.5 0 010 3" />
    <path strokeLinecap="round" d="M8 20c0-2.5 1.8-4 4-4s4 1.5 4 4" />
    <path strokeLinecap="round" d="M9.5 9.5c.3.6.8.6 1.2 0" />
    <path strokeLinecap="round" d="M13.3 9.5c.3.6.8.6 1.2 0" />
  </svg>
)

const LocationIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <circle cx={12} cy={9} r={2.5} strokeWidth={1.5} />
  </svg>
)

const ObjectIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinejoin="round" strokeWidth={1.5} d="M12 2L3 7v10l9 5 9-5V7l-9-5z" />
    <path strokeWidth={1.5} d="M12 12L3 7m9 5l9-5m-9 5v10" />
  </svg>
)

const ImageIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <rect x={3} y={3} width={18} height={18} rx={3} strokeWidth={1.5} />
    <path d="M3 17l6-7 12 8" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    <circle cx={16} cy={8.5} r={2} strokeWidth={1.5} />
  </svg>
)

const VideoIcon = (
  <svg viewBox="0 0 24 24" fill="none">
    <rect x={3} y={3} width={18} height={18} rx={3} stroke="currentColor" strokeWidth={1.5} />
    <path d="M10 8.5v7l6-3.5-6-3.5z" fill="currentColor" />
  </svg>
)

const AudioIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeWidth={2} d="M7 12c0 0 .5-4 1.5-4S10 16 11 16s1-8 2-8 1 8 2 8 1-4 1.5-4 .5 0 .5 0" />
  </svg>
)

const ScriptIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
)

const GroupIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <rect x={4} y={4} width={7} height={7} rx={1.5} strokeWidth={1.5} />
    <rect x={13} y={4} width={7} height={7} rx={1.5} strokeWidth={1.5} />
    <rect x={4} y={13} width={7} height={7} rx={1.5} strokeWidth={1.5} />
    <rect x={13} y={13} width={7} height={7} rx={1.5} strokeWidth={1.5} />
  </svg>
)

// ─── Lookup maps ────────────────────────────────────────────────────────────

/** React element icons keyed by frontend node type (camelCase). */
export const NODE_TYPE_ICON: Record<string, React.ReactNode> = {
  characterDesigner: CharacterIcon,
  locationDesigner: LocationIcon,
  objectDesigner: ObjectIcon,
  imageGenerator: ImageIcon,
  image: ImageIcon,
  videoGenerator: VideoIcon,
  video: VideoIcon,
  videoEditor: VideoIcon,
  audio: AudioIcon,
  soundExpert: AudioIcon,
  scriptWriter: ScriptIcon,
  nodeGroup: GroupIcon,
}

/** Raw SVG HTML strings keyed by frontend node type (camelCase). */
export const NODE_TYPE_ICON_SVG: Record<string, string> = {
  characterDesigner: CHARACTER_SVG,
  locationDesigner: LOCATION_SVG,
  objectDesigner: OBJECT_SVG,
  imageGenerator: IMAGE_SVG,
  image: IMAGE_SVG,
  videoGenerator: VIDEO_SVG,
  video: VIDEO_SVG,
  videoEditor: VIDEO_SVG,
  audio: AUDIO_SVG,
  soundExpert: AUDIO_SVG,
  scriptWriter: SCRIPT_SVG,
  nodeGroup: GROUP_SVG,
}
